export type GrantOpportunityTaskKind = "general" | "report" | "deliverable" | "compliance";

export type GrantOpportunityTask = {
  id: number;
  grant_opportunity_id: number;
  grant_opportunity_milestone_id: number;
  user_id: number;
  assignee_id: number | null;
  assignee_name: string | null;
  title: string;
  kind: GrantOpportunityTaskKind;
  due_at: string | null;
  completed_at: string | null;
  position: number;
  created_at: string;
  updated_at: string;
};

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function tasksPath(municipalityId: number, opportunityId: number): string {
  return `/api/v1/municipalities/${municipalityId}/grant_opportunities/${opportunityId}/tasks`;
}

export async function listGrantOpportunityTasks(
  municipalityId: number,
  opportunityId: number,
): Promise<GrantOpportunityTask[]> {
  const response = await fetch(tasksPath(municipalityId, opportunityId), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error("Failed to load milestones");
  }
  return response.json();
}

export async function createGrantOpportunityTask(
  municipalityId: number,
  opportunityId: number,
  payload: {
    title: string;
    due_at?: string | null;
    assignee_id?: number | null;
    kind?: GrantOpportunityTaskKind;
    grant_opportunity_milestone_id: number;
  },
): Promise<GrantOpportunityTask> {
  const response = await fetch(tasksPath(municipalityId, opportunityId), {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ grant_opportunity_task: payload }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.join(", ") ?? "Failed to create milestone");
  }
  return response.json();
}

export async function updateGrantOpportunityTask(
  municipalityId: number,
  opportunityId: number,
  taskId: number,
  payload: Partial<Pick<GrantOpportunityTask, "title" | "due_at" | "completed_at" | "position" | "assignee_id">>,
): Promise<GrantOpportunityTask> {
  const response = await fetch(`${tasksPath(municipalityId, opportunityId)}/${taskId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ grant_opportunity_task: payload }),
  });
  if (!response.ok) {
    throw new Error("Failed to update milestone");
  }
  return response.json();
}

export async function deleteGrantOpportunityTask(
  municipalityId: number,
  opportunityId: number,
  taskId: number,
): Promise<void> {
  const response = await fetch(`${tasksPath(municipalityId, opportunityId)}/${taskId}`, {
    method: "DELETE",
    credentials: "include",
    headers: { "X-CSRF-Token": csrfToken() },
  });
  if (!response.ok) {
    throw new Error("Failed to delete milestone");
  }
}
